// 攝影機時區（ls-loop MW 待做 → 2026-10-09 補）：時間一律以「那台攝影機」的時區顯示，對齊 Android 觀看 App 的 CamTz。
// 裝置的 tz 是 POSIX 字串（signin 帶上來 / App 設定）→ ianaForPosix 轉 IANA。apiFetch 取裝置時順手記下（rememberDeviceTz）。
// 不知道攝影機時區（舊裝置沒 tz、還沒抓到）→ undefined = 用這台電腦的時區（原本的行為）。
import { ianaForPosix } from './phoneCam';

const zones = new Map<string, string>();

export function rememberDeviceTz(d: { device_id?: string | null; tz?: string | null } | null | undefined): void {
  if (!d?.device_id) return;
  const z = ianaForPosix(d.tz);
  if (z) zones.set(d.device_id, z);
}

/** 這台攝影機的 IANA 時區；不知道 = undefined（用電腦時區）。 */
export function camZone(deviceId?: string | null): string | undefined {
  return deviceId ? zones.get(deviceId) : undefined;
}

/** 幾台攝影機共用的時區：全部知道而且相同才回，否則 undefined（一條時間軸只能用一個時區）。 */
export function sharedZone(deviceIds: string[]): string | undefined {
  if (deviceIds.length === 0) return undefined;
  const zs = deviceIds.map((id) => zones.get(id));
  return zs.every((z) => z && z === zs[0]) ? zs[0] : undefined;
}

/** zone 在 atUs 那一刻相對 UTC 的時差（µs，東邊為正）；zone 沒給 = 這台電腦。 */
export function zoneOffsetUs(zone: string | undefined, atUs: number = Date.now() * 1000): number {
  const at = new Date(atUs / 1000);
  if (!zone) return -at.getTimezoneOffset() * 60_000_000;
  try {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
      }).formatToParts(at).map((x) => [x.type, x.value]),
    );
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return (asUtc - Math.floor(at.getTime() / 1000) * 1000) * 1000;
  } catch {
    return -at.getTimezoneOffset() * 60_000_000;
  }
}

/** "YYYY-MM-DD" 在 zone 的那一天（[00:00, 隔天 00:00)）。 */
export function dayWindowInZone(dateStr: string, zone: string | undefined): { fromUs: number; toUs: number } {
  const [y, m, d] = dateStr.split('-').map(Number);
  const midnightAsUtcUs = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) * 1000;
  const fromUs = midnightAsUtcUs - zoneOffsetUs(zone, midnightAsUtcUs);
  const nextAsUtcUs = midnightAsUtcUs + 86_400_000_000;
  return { fromUs, toUs: nextAsUtcUs - zoneOffsetUs(zone, nextAsUtcUs) };
}

/** zone 的「今天」"YYYY-MM-DD"。 */
export function todayInZone(zone: string | undefined, nowUs: number = Date.now() * 1000): string {
  const d = new Date((nowUs + zoneOffsetUs(zone, nowUs)) / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
